import classNames from 'classnames';
import * as inflection from 'inflection';
import React, { Fragment, useContext, useMemo } from 'react';
import * as styles from './FoldedComments.module.scss';

const LineContext = React.createContext({
  shouldShowTopLine: false,
  shouldShowBottomLine: false,
});

interface LineContextProviderProps {
  showTopLine: boolean;
  showBottomLine: boolean;
  children: React.ReactNode;
}

const LineContextProvider = ({
  showTopLine,
  showBottomLine,
  children,
}: LineContextProviderProps) => {
  const contextValue = useMemo(
    () => ({
      shouldShowTopLine: showTopLine,
      shouldShowBottomLine: showBottomLine,
    }),
    [showTopLine, showBottomLine],
  );

  return (
    <LineContext.Provider value={contextValue}>
      <>{children}</>
    </LineContext.Provider>
  );
};

export const defaultRenderMoreText = (count: number) =>
  `${count} more ${inflection.inflect('comment', count)}`;

interface FoldedCommentsProps<T extends { id: string }> {
  expanded: boolean;
  showLineWhenFolded?: boolean;
  limit?: number;
  comments: T[];
  renderComment: (comment: T, index: number) => React.ReactNode;
  renderMoreText?: (count: number) => React.ReactNode;
}

const FoldedComments = <T extends { id: string }>({
  expanded,
  showLineWhenFolded = false,
  limit = 2,
  comments,
  renderComment,
  renderMoreText = defaultRenderMoreText,
}: FoldedCommentsProps<T>) => {
  const moreCount = comments.length - limit;
  const moreText =
    !expanded && moreCount > 0 ? renderMoreText(moreCount) : null;

  return (
    <div
      className={classNames('comments', styles.container, {
        [styles.isExpanded]: expanded,
        [styles.showLineWhenFolded]: showLineWhenFolded,
      })}
    >
      {comments.map((comment, idx) => {
        if (moreText) {
          if (idx === 0) {
            return (
              <Fragment key={comment.id}>
                <LineContextProvider
                  key={comment.id}
                  showBottomLine={showLineWhenFolded && !expanded}
                  showTopLine={false}
                >
                  {renderComment(comment, idx)}
                </LineContextProvider>
                <div
                  className={classNames(
                    'comment-more-count',
                    styles.moreCountContainer,
                  )}
                >
                  <div className={styles.link}>{moreText}</div>
                </div>
              </Fragment>
            );
          }
          if (idx < comments.length - limit + 1) {
            return null;
          }
        }

        return (
          <LineContextProvider
            key={comment.id}
            showBottomLine={
              showLineWhenFolded &&
              !expanded &&
              idx === 0 &&
              comments.length > 1
            }
            showTopLine={
              showLineWhenFolded &&
              !expanded &&
              comments.length > 1 &&
              idx === comments.length - 1
            }
          >
            <>{renderComment(comment, idx)}</>
          </LineContextProvider>
        );
      })}
    </div>
  );
};

interface LineContainer {
  children: React.ReactNode;
}

const LineContainer = ({ children }: LineContainer) => {
  const { shouldShowTopLine, shouldShowBottomLine } = useContext(LineContext);

  return (
    <div className={styles.lineContainer}>
      {shouldShowTopLine && <div className={styles.lineTop} />}
      {children}
      {shouldShowBottomLine && <div className={styles.lineBottom} />}
    </div>
  );
};

export default { Root: FoldedComments, LineContainer };
